import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, CreditCard, Truck, Check, AlertCircle, Loader2, ChevronLeft, Shield, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { resolveProductImage, productHasImage } from '../lib/productImages';
import ProductImagePlaceholder from '../components/ProductImagePlaceholder';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [cart, setCart] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: user?.email || '',
    phone: '',
    shipping_address: ''
  });

  useEffect(() => {
    // Get cart from location state or localStorage
    const cartData = location.state?.cart || JSON.parse(localStorage.getItem('cart') || '[]');
    if (cartData.length === 0) {
      toast.error('Your cart is empty');
      navigate('/shop');
      return;
    }
    setCart(cartData);
    
    // Load saved customer info
    fetchCustomerInfo();
  }, [location.state, navigate]);

  const fetchCustomerInfo = async () => {
    try {
      const response = await axios.get(`${API}/customer/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Object.keys(response.data).length > 0) {
        setCustomerInfo(prev => ({
          ...prev,
          name: response.data.name || prev.name,
          email: response.data.email || prev.email,
          phone: response.data.phone || prev.phone,
          shipping_address: response.data.shipping_address || prev.shipping_address
        }));
      }
    } catch (error) {
      console.error('Failed to fetch customer info:', error);
    } finally {
      setLoadingCustomer(false);
    }
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.retail_price * item.quantity), 0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!customerInfo.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    if (!customerInfo.email.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    if (!customerInfo.phone.trim() || customerInfo.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    if (!customerInfo.shipping_address.trim() || customerInfo.shipping_address.length < 10) {
      toast.error('Please enter a complete shipping address');
      return false;
    }
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return false;
    }
    return true;
  };

  const handleCheckout = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const checkoutData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        customer: customerInfo,
        origin_url: window.location.origin,
        terms_accepted: termsAccepted
      };

      console.log('Sending checkout request to /api/payment/create-checkout...');
      
      const response = await axios.post(`${API}/payment/create-checkout`, checkoutData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Checkout response received:', response.data);

      // Validate we received a URL
      if (!response.data || !response.data.url) {
        console.error('No URL in response:', response.data);
        alert('Error: No checkout URL received from server');
        setLoading(false);
        return;
      }

      // Clear cart before redirect
      localStorage.removeItem('cart');
      
      console.log('Redirecting to Stripe:', response.data.url);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Checkout failed. Please try again.';
      alert(`Checkout Error: ${errorMessage}`);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  if (loadingCustomer) {
    return (
      <div className="min-h-screen bg-[#050605] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#c8b273] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050605] py-8 px-4 md:px-8" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/shop')}
            className="text-[#8a948a] hover:text-white"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back to Shop
          </Button>
        </div>

        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase tracking-wider text-white mb-8">
          Checkout
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Customer Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Details */}
            <div className="glass-card p-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#c8b273]" />
                Delivery Details
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#8a948a] mb-1 block">Full Name *</label>
                  <Input
                    name="name"
                    value={customerInfo.name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className="bg-[#0b0d0b] border-white/10 text-white"
                    data-testid="checkout-name"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#8a948a] mb-1 block">Email *</label>
                  <Input
                    name="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className="bg-[#0b0d0b] border-white/10 text-white"
                    data-testid="checkout-email"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#8a948a] mb-1 block">Phone Number *</label>
                  <Input
                    name="phone"
                    value={customerInfo.phone}
                    onChange={handleInputChange}
                    placeholder="07123 456789"
                    className="bg-[#0b0d0b] border-white/10 text-white"
                    data-testid="checkout-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-[#8a948a] mb-1 block">Shipping Address *</label>
                  <textarea
                    name="shipping_address"
                    value={customerInfo.shipping_address}
                    onChange={handleInputChange}
                    placeholder="123 Fishing Lane&#10;Lakeside Village&#10;County&#10;AB12 3CD"
                    rows={4}
                    className="w-full bg-[#0b0d0b] border border-white/10 rounded-md px-3 py-2 text-white placeholder:text-[#4a504a] focus:border-[#c8b273] focus:outline-none"
                    data-testid="checkout-address"
                  />
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="glass-card p-6">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#c8b273]" />
                Order Items ({cart.length})
              </h2>
              
              <div className="space-y-4">
                {cart.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0">
                    {productHasImage(item) ? (
                      <img
                        src={resolveProductImage(item)}
                        alt={item.product_name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded overflow-hidden">
                        <ProductImagePlaceholder category={item.category} compact />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-white font-medium">{item.product_name}</p>
                      <p className="text-[#8a948a] text-sm">{item.brand_name}</p>
                      <p className="text-[#6b7d6b] text-xs">SKU: {item.sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white">x{item.quantity}</p>
                      <p className="text-[#c8b273] font-semibold">£{(item.retail_price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Terms & Conditions */}
            <div className="glass-card p-6">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-1 border-[#c8b273] data-[state=checked]:bg-[#c8b273] data-[state=checked]:text-black"
                  data-testid="terms-checkbox"
                />
                <label htmlFor="terms" className="text-sm text-[#8a948a] cursor-pointer">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" className="text-[#c8b273] hover:underline">Terms and Conditions</a>,{' '}
                  <a href="/privacy" target="_blank" className="text-[#c8b273] hover:underline">Privacy Policy</a>,{' '}
                  <a href="/returns" target="_blank" className="text-[#c8b273] hover:underline">Returns Policy</a>, and{' '}
                  <a href="/delivery" target="_blank" className="text-[#c8b273] hover:underline">Delivery Policy</a>.
                </label>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="glass-card p-6 sticky top-24">
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white mb-4">
                Order Summary
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#8a948a]">
                  <span>Subtotal</span>
                  <span>£{calculateTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#8a948a]">
                  <span>Shipping</span>
                  <span className="text-green-500">FREE</span>
                </div>
                <div className="border-t border-white/10 pt-3">
                  <div className="flex justify-between text-white text-lg font-semibold">
                    <span>Total</span>
                    <span className="text-[#c8b273]">£{calculateTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={loading || !termsAccepted}
                className="w-full btn-primary h-12 text-lg"
                data-testid="pay-now-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay Now
                  </>
                )}
              </Button>

              {/* Payment Methods */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-[#6b7d6b] text-sm mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Secure payment powered by Stripe
                </p>
                <div className="flex items-center gap-3 justify-center">
                  <div className="bg-white/10 rounded px-3 py-1.5">
                    <span className="text-xs text-white font-medium">VISA</span>
                  </div>
                  <div className="bg-white/10 rounded px-3 py-1.5">
                    <span className="text-xs text-white font-medium">Mastercard</span>
                  </div>
                  <div className="bg-white/10 rounded px-3 py-1.5">
                    <span className="text-xs text-white font-medium">Apple Pay</span>
                  </div>
                  <div className="bg-white/10 rounded px-3 py-1.5">
                    <span className="text-xs text-white font-medium">Google Pay</span>
                  </div>
                </div>
              </div>

              {/* Security Note */}
              <div className="mt-4 flex items-center gap-2 text-[#6b7d6b] text-xs">
                <Lock className="w-3 h-3" />
                <span>Your card details are never stored on our servers</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
